import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Bell, Loader2, Save, RotateCcw, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSeuilsAlertes } from '@/hooks/useSeuilsAlertes';
import { SeuilsAlertes, DEFAULT_SEUILS, SEUILS_CONFIG } from '@/types/alertes';

export default function AlertesSettings() {
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [loadingEntreprise, setLoadingEntreprise] = useState(true);

  // Hook pour les seuils
  const {
    seuils,
    isCustom,
    isLoading,
    isSaving,
    saveSeuils,
    resetToDefaults,
  } = useSeuilsAlertes(entrepriseId);

  // État local pour le formulaire (permet de modifier sans sauvegarder immédiatement)
  const [localSeuils, setLocalSeuils] = useState<SeuilsAlertes>(DEFAULT_SEUILS);
  const [localCustom, setLocalCustom] = useState(false);

  // Charger l'entreprise au montage
  useEffect(() => {
    const loadEntreprise = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoadingEntreprise(false);
        return;
      }

      const { data } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (data) {
        setEntrepriseId(data.id);
      }
      setLoadingEntreprise(false);
    };

    loadEntreprise();
  }, []);

  // Synchroniser les états locaux avec les données chargées
  useEffect(() => {
    setLocalSeuils(seuils);
    setLocalCustom(isCustom);
  }, [seuils, isCustom]);

  // Mettre à jour un seuil localement
  const updateSeuil = (key: keyof SeuilsAlertes, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setLocalSeuils(prev => ({ ...prev, [key]: numValue }));
    }
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    await saveSeuils(localSeuils, localCustom);
  };

  // Réinitialiser aux valeurs par défaut
  const handleReset = async () => {
    setLocalSeuils(DEFAULT_SEUILS);
    setLocalCustom(false);
    await resetToDefaults();
  };

  // Toggle du mode personnalisé
  const handleToggleCustom = (checked: boolean) => {
    setLocalCustom(checked);
    if (!checked) {
      // Si on désactive, revenir aux valeurs par défaut
      setLocalSeuils(DEFAULT_SEUILS);
    }
  };

  // Vérifier si des modifications ont été faites
  const hasChanges = JSON.stringify(localSeuils) !== JSON.stringify(seuils) || localCustom !== isCustom;

  if (loadingEntreprise || isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!entrepriseId) {
    return (
      <Alert>
        <AlertDescription>
          Vous devez d'abord créer une entreprise pour configurer les alertes.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h2 className="text-2xl font-bold">Alertes</h2>
        <p className="text-muted-foreground mt-1">
          Configurez les seuils qui déclenchent les alertes pour votre entreprise.
        </p>
      </div>

      {/* Card principale */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle>Seuils d'alertes</CardTitle>
              <CardDescription>
                Personnalisez les seuils qui déclenchent les différentes alertes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Switch principal */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div>
              <p className="font-medium">Personnaliser les seuils d'alertes</p>
              <p className="text-sm text-muted-foreground">
                {localCustom
                  ? 'Vos seuils personnalisés sont actifs'
                  : 'Utilise les seuils par défaut optimisés pour le BTP'}
              </p>
            </div>
            <Switch
              checked={localCustom}
              onCheckedChange={handleToggleCustom}
            />
          </div>

          {/* Info sur les défauts */}
          {!localCustom && (
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                Les seuils par défaut sont optimisés pour le secteur du BTP français.
                Activez la personnalisation pour les adapter à votre activité.
              </AlertDescription>
            </Alert>
          )}

          {/* Formulaire (visible seulement si switch ON) */}
          {localCustom && (
            <div className="space-y-8">
              {/* Boucle sur les catégories */}
              {Object.entries(SEUILS_CONFIG).map(([catKey, category]) => (
                <div key={catKey} className="space-y-4">
                  <h4 className="font-medium text-sm border-b pb-2">{category.label}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.fields.map(field => (
                      <div key={field.key} className="space-y-1.5">
                        <Label htmlFor={field.key}>{field.label}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={field.key}
                            type="number"
                            min={field.min ?? 0}
                            max={field.max}
                            value={localSeuils[field.key]}
                            onChange={(e) => updateSeuil(field.key, e.target.value)}
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground">{field.unit}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{field.hint}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
            {localCustom && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={isSaving}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Réinitialiser les valeurs par défaut
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Légende des alertes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comprendre les alertes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span><strong>Critique (rouge)</strong> : Action immédiate requise</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span><strong>Attention (orange)</strong> : À surveiller</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span><strong>Info (bleu)</strong> : Information utile</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span><strong>Suggestion (violet)</strong> : Optimisation possible</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
